import { ICellRendererParams } from '@ag-grid-community/all-modules';
import { useQuery } from '@apollo/client';
import { createStyles, IconButton, lighten, makeStyles, Tab, Tabs, Theme, useTheme } from '@material-ui/core';
import { Add, DeleteOutline, InfoOutlined, Stars } from '@material-ui/icons';
import classNames from 'classnames';
import React, { CSSProperties, useMemo } from 'react';
import Select, { components, ControlProps, StylesConfig } from 'react-select';

import { GetAllSkills as GetAllSkillsType } from '../../../__generated__/types';
import TnAgGrid from '../../../_components/tn-ag-grid/TnAgGrid';
import TnButton from '../../../_components/tn-button/TnButton';
import TnTypography from '../../../_components/tn-typography/TnTypography';
import { GetAllSkills } from '../../../_services/candidates/queries';
import MoveToStandoutIcon from '../../../static/_assets/active-quick-actions/arrow-left-to-bracket.svg';
import MoveToAdditionalIcon from '../../../static/_assets/active-quick-actions/arrow-right-to-bracket.svg';
import DeleteIcon from '../../../static/_assets/active-quick-actions/delete.svg';

export interface CandidateSkill {
  name: string;
  proficiency_rating: number | null;
  standout_skill: boolean;
  verified: boolean;
  delete_icon: string;
}

interface Skill {
  name: string;
}

interface SkillOption {
  label: string;
  value: string;
}

interface SkillTableChangeHandlers {
  onAddSkill: (skill: Skill, standout: boolean) => void;
  onChangeSkillProficiency: (skillName: string, proficiency: number) => void;
  onMoveSkills: (skillNames: string[], standout: boolean) => void;
  onDeleteSkills: (skillNames: string[]) => void;
}

interface Props {
  skills: CandidateSkill[];
  changeHandlers: SkillTableChangeHandlers;
  onClickLearnMore?: () => void;
  addSkillDisabled?: boolean;
}

type SkillTab = 'standout' | 'additional';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectedStar: {
      color: '#FFCE62',
      cursor: 'pointer',
    },
    unselectedStar: {
      color: '#828c95',
      cursor: 'pointer',
    },
    deleteIconContainer: {
      cursor: 'pointer',
      border: `1px solid ${lighten('#B00020', 0.5)}`,
      padding: '4px 4px 0px 4px',
      borderRadius: 4,
    },
    deleteIcon: {
      fill: '#B00020',
    },
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    helperContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: 'calc(100% - 16px)',
      border: `1px solid ${lighten('#000000', 0.8)}`,
      borderRadius: 4,
      padding: 8,
      marginBottom: 16,
    },
    tooltip: {
      color: theme.palette.primary.main,
    },
    textAndIcon: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    tabs: {
      borderBottom: `1px solid ${lighten('#000000', 0.8)}`,
      marginBottom: 16,
    },
    tab: {
      textTransform: 'none',
      minWidth: 0,
      fontSize: 16,
    },
    tabLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    tabCount: {
      borderRadius: 10,
      padding: '0 8px',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '20px',
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.primary,
    },
    activeTabCount: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    addSkillContainer: {
      width: 390,
    },
    addSkillIcon: {
      color: theme.palette.primary.main,
      marginLeft: 12,
    },
  }),
);

interface ProficiencyCellRendererProps {
  data: CandidateSkill;
  onChangeSkillProficiency: (skillName: string, proficiency: number) => void;
}

const ProficiencyCellRenderer = React.memo(
  ({ data, onChangeSkillProficiency }: Readonly<ProficiencyCellRendererProps>) => {
    const classes = useStyles();
    const rating = data.proficiency_rating ?? 0;

    const handleClick = React.useCallback(
      (index: number) => {
        onChangeSkillProficiency(data.name, index + 1);
      },
      [onChangeSkillProficiency, data.name],
    );

    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <Stars
            key={index}
            className={classNames({
              [classes.selectedStar]: index < rating,
              [classes.unselectedStar]: index >= rating,
            })}
            onClick={() => handleClick(index)}
          />
        ))}
      </>
    );
  },
);

const AddSkillControl = ({ children, ...props }: ControlProps<SkillOption>) => {
  const classes = useStyles();

  return (
    <components.Control {...props}>
      <Add className={classes.addSkillIcon} />
      {children}
    </components.Control>
  );
};

const addSkillSelectStyles = (theme: Theme): StylesConfig => ({
  control: (provided: CSSProperties) => ({
    ...provided,
    minHeight: 40,
    borderColor: theme.palette.primary.main,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  }),
  placeholder: (provided: CSSProperties) => ({
    ...provided,
    color: theme.palette.text.secondary,
  }),
  input: (provided: CSSProperties) => ({
    ...provided,
    color: theme.palette.text.primary,
  }),
  menu: (provided: CSSProperties) => ({
    ...provided,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  }),
  menuPortal: (provided: CSSProperties) => ({
    ...provided,
    zIndex: 10000000,
  }),
});

interface AddSkillSelectProps {
  placeholder: string;
  options: SkillOption[];
  disabled?: boolean;
  onSelect: (skill: Skill) => void;
}

const AddSkillSelect = ({ placeholder, options, disabled, onSelect }: Readonly<AddSkillSelectProps>) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div className={classes.addSkillContainer}>
      <Select
        value={null}
        options={options}
        placeholder={placeholder}
        isSearchable
        isDisabled={disabled}
        styles={addSkillSelectStyles(theme)}
        components={{ Control: AddSkillControl, DropdownIndicator: null, IndicatorSeparator: null }}
        menuPortalTarget={document.body}
        onChange={(option) => {
          const selected = option as SkillOption | null;
          if (selected) {
            onSelect({ name: selected.label });
          }
        }}
      />
    </div>
  );
};

export default function SkillsTable({ skills, changeHandlers, onClickLearnMore, addSkillDisabled }: Readonly<Props>) {
  const classes = useStyles();

  const [selectedRows, setSelectedRows] = React.useState<(CandidateSkill & { id: string })[]>([]);
  const [activeTab, setActiveTab] = React.useState<SkillTab>('standout');
  const isStandoutTab = activeTab === 'standout';

  const { data: skillData } = useQuery<GetAllSkillsType>(GetAllSkills);

  const standoutSkills = useMemo(() => skills.filter((skill) => skill.standout_skill), [skills]);
  const additionalSkills = useMemo(() => skills.filter((skill) => !skill.standout_skill), [skills]);
  const tabSkills = isStandoutTab ? standoutSkills : additionalSkills;

  const skillOptions = useMemo<SkillOption[]>(() => {
    const tabSkillNames = new Set(tabSkills.map((skill) => skill.name.toLowerCase()));
    const uniqueSkills = new Map<string, SkillOption>();

    (skillData?.getSkills ?? []).forEach((skill) => {
      const key = skill.name.toLowerCase();
      if (!tabSkillNames.has(key) && !uniqueSkills.has(key)) {
        uniqueSkills.set(key, { label: skill.name, value: skill.name });
      }
    });

    return Array.from(uniqueSkills.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [skillData, tabSkills]);

  const columnHeaders = useMemo(() => {
    const deleteColumn = {
      headerName: '',
      field: 'delete_icon',
      width: 70,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        innerRendererFramework: (params: ICellRendererParams) => {
          return (
            <div
              aria-hidden="true"
              onClick={() => changeHandlers.onDeleteSkills([params.data.name])}
              className={classes.deleteIconContainer}
            >
              <DeleteOutline className={classes.deleteIcon} />
            </div>
          );
        },
      },
    };

    const nameColumn = {
      field: 'name',
      headerName: 'Skill Name',
      checkboxSelection: true,
      cellRenderer: 'agGroupCellRenderer',
      width: 300,
    };

    if (!isStandoutTab) {
      return [nameColumn, deleteColumn];
    }

    return [
      nameColumn,
      {
        field: 'proficiency_rating',
        headerName: 'Click to Rate Skill',
        cellRenderer: 'agGroupCellRenderer',
        width: 250,
        cellRendererParams: {
          innerRendererFramework: (params: ICellRendererParams) => (
            <ProficiencyCellRenderer
              data={params.data}
              onChangeSkillProficiency={changeHandlers.onChangeSkillProficiency}
            />
          ),
        },
      },
      deleteColumn,
    ];
  }, [isStandoutTab]);

  const iconList = useMemo(() => {
    const selectedSkillNames = selectedRows.map((row) => row.id);

    return [
      isStandoutTab
        ? {
            icon: MoveToAdditionalIcon,
            disabledIcon: MoveToAdditionalIcon,
            key: 'move_to_additional',
            name: 'Move to Additional',
            isDisabled: false,
            onClick: () => {
              changeHandlers.onMoveSkills(selectedSkillNames, false);
              setSelectedRows([]);
            },
          }
        : {
            icon: MoveToStandoutIcon,
            disabledIcon: MoveToStandoutIcon,
            key: 'move_to_standout',
            name: 'Move to Standout',
            isDisabled: false,
            onClick: () => {
              changeHandlers.onMoveSkills(selectedSkillNames, true);
              setSelectedRows([]);
            },
          },
      {
        icon: DeleteIcon,
        disabledIcon: DeleteIcon,
        key: 'delete_contractor_project',
        name: 'Delete',
        isDisabled: false,
        onClick: () => {
          changeHandlers.onDeleteSkills(selectedSkillNames);
          setSelectedRows([]);
        },
      },
    ];
  }, [selectedRows, isStandoutTab]);

  const rowData = useMemo(() => {
    return tabSkills.map((skill) => ({
      id: skill.name,
      ...skill,
    }));
  }, [tabSkills]);

  const renderTabLabel = (label: string, count: number, tab: SkillTab) => (
    <span className={classes.tabLabel}>
      {label}
      <span className={classNames(classes.tabCount, { [classes.activeTabCount]: activeTab === tab })}>{count}</span>
    </span>
  );

  return (
    <>
      <div className={classes.labelContainer}>
        <TnTypography bold variant="body">
          Candidate Skills & Rating
        </TnTypography>
      </div>
      <div className={classes.helperContainer}>
        <div className={classes.textAndIcon}>
          <IconButton>
            <InfoOutlined className={classes.tooltip} />
          </IconButton>
          <TnTypography variant="caption2">
            Pick out standout skills and rate them. All other skills will be saved as Additional skills.
          </TnTypography>
        </div>
        <TnButton
          variant="outlined"
          color="secondary"
          value="Learn More about Skill Ratings"
          onClick={() => onClickLearnMore?.()}
        />
      </div>
      <Tabs
        value={activeTab}
        onChange={(_, tab: SkillTab) => {
          setActiveTab(tab);
          setSelectedRows([]);
        }}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab
          value="standout"
          className={classes.tab}
          label={renderTabLabel('Standout', standoutSkills.length, 'standout')}
          data-testid="standout_skills_tab"
        />
        <Tab
          value="additional"
          className={classes.tab}
          label={renderTabLabel('Additional', additionalSkills.length, 'additional')}
          data-testid="additional_skills_tab"
        />
      </Tabs>
      <TnAgGrid
        key={activeTab}
        columnHeaders={columnHeaders}
        rowData={rowData}
        iconList={iconList}
        isCheckboxRequired={false}
        pagination={true}
        displayQuickActions={true}
        paginationPageSize={10}
        length={500}
        width={'100%'}
        rowsPerPage={10}
        rowsPerPageOptions={[10, 15, 25]}
        immutableData
        searchPlaceholder={isStandoutTab ? 'Filter standout skills' : 'Filter additional skills'}
        action={
          <AddSkillSelect
            placeholder={isStandoutTab ? 'Add a standout skill' : 'Add an additional skill'}
            options={skillOptions}
            disabled={addSkillDisabled}
            onSelect={(skill) => changeHandlers.onAddSkill(skill, isStandoutTab)}
          />
        }
        onChange={(rows: (CandidateSkill & { id: string })[]) => setSelectedRows(rows)}
      />
    </>
  );
}
